'use client';

import { useQuery } from '@tanstack/react-query';

import { getProductUrl, type ProductHit } from '@libs/algolia';
import { getBuyItAgainGuest, getProductsByIds } from '@libs/api-proxy-client';
import type { BuyItAgainGuestApiResponse, BuyItAgainGuestPlacement } from '@libs/common-types';
import { getLocale, getVisitorId, logger } from '@libs/core';

const OPTEX_BIA_GUEST_QUERY_KEY = 'optex-bia-guest';
const OPTEX_BIA_GUEST_COUNT = 20;

export type OptexBiaGuestProduct = {
    sku: string;
    recRank: number;
    hit: ProductHit;
    name: string;
    imageUrl: string;
    href: string;
};

type OptexBiaGuestOptions = Readonly<{
    optexEnabled: boolean;
    optexPlacement: BuyItAgainGuestPlacement;
    optexCaller: string;
}>;

function mapOptexBiaGuestProducts(
    optexRecommendations: BuyItAgainGuestApiResponse,
    optexHits: ProductHit[],
    optexCaller: string
): OptexBiaGuestProduct[] {
    const optexHitsBySku = new Map(optexHits.filter(Boolean).map(optexHit => [optexHit.objectID, optexHit]));
    const optexProducts = optexRecommendations.flatMap(optexRecommendation => {
        const optexHit = optexHitsBySku.get(optexRecommendation.sku_nbr);
        const optexImageUrl = optexHit?.images?.small ?? optexHit?.images?.large;

        if (!optexHit?.name || !optexHit.masterProductID || !optexImageUrl) return [];

        return [
            {
                sku: optexRecommendation.sku_nbr,
                recRank: optexRecommendation.rec_rank,
                hit: optexHit,
                name: optexHit.name,
                imageUrl: optexImageUrl,
                href: getProductUrl(optexHit),
            },
        ];
    });

    if (optexProducts.length < optexRecommendations.length) {
        logger.warn(
            `[OPTEX BIA GUEST][Warn][${optexCaller}]: Resolved ${optexProducts.length} of ${optexRecommendations.length} recommendations; rendering resolved products`
        );
    }

    return optexProducts;
}

async function fetchOptexBiaGuestProducts(
    optexPlacement: BuyItAgainGuestPlacement,
    optexCaller: string
): Promise<OptexBiaGuestProduct[]> {
    try {
        const optexRecommendations = await getBuyItAgainGuest({
            placement: optexPlacement,
            count: OPTEX_BIA_GUEST_COUNT,
        });
        if (optexRecommendations.length === 0) return [];

        const optexHits = await getProductsByIds({
            ids: optexRecommendations.map(optexRecommendation => optexRecommendation.sku_nbr),
            caller: optexCaller,
        });
        return mapOptexBiaGuestProducts(optexRecommendations, optexHits, optexCaller);
    } catch (optexError) {
        logger.error(`[OPTEX BIA GUEST][Error][${optexCaller}]: Failed to load products`, optexError);
        throw optexError;
    }
}

export function useOptexBiaGuest({ optexEnabled, optexPlacement, optexCaller }: OptexBiaGuestOptions) {
    const optexVisitorId = getVisitorId();

    return useQuery({
        queryKey: [OPTEX_BIA_GUEST_QUERY_KEY, optexVisitorId, getLocale(), optexPlacement],
        queryFn: () => fetchOptexBiaGuestProducts(optexPlacement, optexCaller),
        enabled: optexEnabled && Boolean(optexVisitorId),
    });
}
